// Content + theme for the portfolio page. Mirrors the Claude Design source.

// ---- Accent theme (default "Acid") -------------------------------------
export const accentMap = {
    Acid: 'oklch(0.91 0.19 112)',
    Tangerine: 'oklch(0.74 0.18 50)',
    Violet: 'oklch(0.62 0.21 300)',
} as const;

export const accentInkMap = {
    Acid: '#14160a',
    Tangerine: '#1f1206',
    Violet: '#ffffff',
} as const;

export type AccentName = keyof typeof accentMap;

export const accent: AccentName = 'Acid';
export const accentColor = accentMap[accent];
export const accentInk = accentInkMap[accent];

export const openToWork = true;

// ---- Contacts ----------------------------------------------------------
export const contacts = {
    email: 'job.roman@shulgin.pro',
    phone: '+972 53 242 9425',
    phoneHref: 'tel:+972532429425',
    linkedin: 'https://www.linkedin.com/in/roman-shulgin-pro/',
    location: 'Haifa / Tel Aviv, Israel',
    cv: 'Roman-Shulgin-CV.pdf',
    handle: 'roman.shulgin.pro',
};

export const education = {
  title: 'B.Sc. Computer Science, MTUCI',
  since: 2011,
  realise: 2016,
  fullName: 'Moscow Technical University Communication and Informatics',
}

// ---- Selected work -----------------------------------------------------
export type Project = {
    name: string;
    url: string;
    badge: string;
    badgeBg: string;
    badgeColor: string;
    accent: string; // top bar + hover border
    description: string;
    tags: string[];
};

export const projects: Project[] = [
    {
        name: 'iHousing',
        url: 'https://ihousing.co.il',
        badge: 'LIVE · PERSONAL',
        badgeBg: 'oklch(0.74 0.18 50 / 0.16)',
        badgeColor: 'oklch(0.8 0.13 55)',
        accent: 'oklch(0.74 0.18 50)',
        description:
            'Full-stack SaaS rental-housing platform. Architected end to end — Next.js frontend, 10 NestJS microservices, GraphQL, and automated Docker delivery.',
        tags: ['Next.js', 'NestJS', 'GraphQL', 'MongoDB'],
    },
    {
        name: 'GiveMeClassic',
        url: 'https://givemeclassic.com',
        badge: 'LIVE · PERSONAL',
        badgeBg: 'oklch(0.52 0.20 300 / 0.18)',
        badgeColor: 'oklch(0.82 0.1 305)',
        accent: 'oklch(0.52 0.20 300)',
        description:
            'A side project for discovering and comparing classical-music recordings — built to scratch a personal itch and explore clean web architecture.',
        tags: ['React', 'Node.js', 'TypeScript'],
    },
    {
        name: 'Observe the Sun',
        url: 'https://observethesun.com',
        badge: 'LIVE · @ PROSCOM',
        badgeBg: 'oklch(0.91 0.19 112 / 0.16)',
        badgeColor: 'oklch(0.88 0.16 110)',
        accent: 'oklch(0.91 0.19 112)',
        description:
            'A companion for tracking real-time solar activity and observation conditions — a playground for live data and responsive UI.',
        tags: ['React', 'Live data'],
    },
];

export type EnterpriseProject = {
    name: string;
    tag: string;
    description: string; // may contain <strong> markup
    tags: string[];
};

export const enterpriseProjects: EnterpriseProject[] = [
    {
        name: 'Riskified',
        tag: 'NDA · ENTERPRISE',
        description:
            'E-commerce fraud-prevention platform. Full-stack work across a <strong>NestJS, React &amp; Angular</strong> stack — cut a legacy linking-table data layer from 10–20s to 0.7–1.8s, built custom AI agents (Mastra + Anthropic) with Golden Dataset evals and Langfuse-based troubleshooting, and architected a JWT→HMAC traffic proxy and request-audit library for strict security compliance.',
        tags: ['AI Agents', 'AWS · K8s', 'Terraform', 'ArgoCD'],
    },
    {
        name: 'Luxoft',
        tag: 'NDA · BANKING',
        description:
            'Banking credit-risk platform. Engineered a front-end risk-monitoring system on a NestJS microservices backend processing high-throughput data via <strong>Kafka &amp; RabbitMQ</strong>, and built an internal React UI library that unified design standards across teams.',
        tags: ['React UI Library', 'Kafka', 'Angular', 'NestJS'],
    },
];

// ---- Experience --------------------------------------------------------
export type Role = {
    title: string;
    client: string;
    meta: string;
    summary: string;
};

export type Job = {
    dates: string;
    location: string;
    title: string;
    company: string;
    summary?: string;
    roles?: Role[];
};

export const jobs: Job[] = [
    {
        dates: '2025 — 2026',
        location: 'Tel Aviv, Israel',
        title: 'Software Developer / AI Platform Engineer',
        company: 'Riskified',
        summary:
            'Worked in a full-stack capacity across a NestJS, React and Angular stack. Optimized a legacy linking-table data layer in a SaaS-based enterprise AI agent, cutting processing time from 10–20s to 0.7–1.8s via Elasticsearch, caching and query reduction; used Claude Code to analyze and restructure the legacy logic. Built and deployed custom AI agents (Mastra + Anthropic), owning prompt engineering, Golden Dataset preparation and evaluation frameworks, and used Langfuse to investigate and resolve AI chat-session issues. Architected a JWT→HMAC traffic proxy and request-audit library, and ran microservices on AWS, Auth0, Kubernetes, Terraform, Groundcover and ArgoCD (including SSO, EKS and subnet configuration) with 24/7 on-call ownership.',
    },
    {
        dates: '2023 — 2025',
        location: 'Remote, Israel',
        title: 'Lead Developer',
        company: 'iHousing (Startup)',
        summary:
            'Architected and developed a full-stack SaaS rental-housing platform — Next.js (TypeScript) SSR frontend with 10 NestJS microservices (MongoDB, GraphQL, RabbitMQ). Established GitLab CI/CD for automated Docker deployments and integrated third-party systems including CRMs and Telegram Bot APIs.',
    },
    {
        dates: '2019 — 2023',
        location: 'Russia → Serbia',
        title: 'Lead, Senior Frontend Developer',
        company: 'Luxoft',
        roles: [
            {
                title: 'Senior Frontend Developer',
                client: 'Credit Suisse',
                meta: '2022 — 2023 · Belgrade',
                summary:
                    'Relocated to Serbia for this engagement. Migrated a portfolio of critical business applications off a legacy proprietary hosting platform onto open-source, cloud-ready architecture, and built an internal UI library that unified application design with highly efficient universal components.',
            },
            {
                title: 'Team Lead',
                client: 'Leroy Merlin',
                meta: '2021 — 2022 · Moscow',
                summary:
                    'Led a full-stack team building React + TypeScript front-ends with Redux-Saga and micro-frontends (module federation) on a NestJS / PostgreSQL backend with RabbitMQ, Kafka and Apigee. Ran SCRUM ceremonies, risk assessment, people management and code review, with Jenkins CI/CD.',
            },
            {
                title: 'Tech Lead',
                client: 'Gazprombank',
                meta: '2019 — 2021 · Moscow',
                summary:
                    'Tech-led OLIMP — a bank platform for credit-risk management and monitoring. Built a React + TypeScript front-end (Redux-Saga local store, Apollo Client server store) over a GraphQL API, partnering with Java developers on service architecture.',
            },
        ],
    },
    {
        dates: '2017 — 2019',
        location: 'Moscow, Russia',
        title: 'Senior Full-Stack Developer',
        company: 'Proscom',
        summary:
            'Full-stack delivery across MedTech, EdTech and gov-tech products — React / Angular front-ends with NodeJS (Express, NestJS) and PostgreSQL back-ends, plus GraphQL, SOAP/XSLT integrations and React Native / Cordova hybrid apps. Clients included Moscow’s EMIAS unified medical system and the Russian Ministry of Education.',
    },
    {
        dates: '2017',
        location: 'Moscow, Russia',
        title: 'Full-Stack Developer & Project Manager',
        company: 'Lemier',
        summary:
            'Built a museum experience that surfaces content from Bluetooth-LE positioning and a barcode product-management app with content and access-rights admin — Android (Java) and iOS (Swift) clients, AngularJS front-ends and a NodeJS/Express + Socket.io backend. As PM: wrote specs, hired and coordinated developers, ran code reviews and set up TeamCity CI/CD.',
    },
    {
        dates: '2016',
        location: 'Fryazino/Moscow, Russia',
        title: 'Full-Stack Developer & Tech Lead',
        company: 'Emmtech',
        summary:
            'Tech-led real-time products on AngularJS + NodeJS/Express + Socket.io — a distance-learning platform with chat, scheduling and recorded video broadcasting, and an online-auction system with live video and chat (Redis for sessions, PostgreSQL for data), plus a social network with an online store.',
    },
    {
        dates: '2014 — 2016',
        location: 'Moscow, Russia',
        title: 'Full-Stack Developer',
        company: 'Vkostume',
        summary:
            'Built a made-to-order carnival-costume e-commerce store with delivery — order administration, live inventory tracking and dispatch to delivery services. PHP (CodeIgniter) backend, MySQL with Memcached caching, a jQuery / Bootstrap front-end, and REST/SOAP integrations with delivery, analytics and ad platforms.',
    },
];

// ---- Stack -------------------------------------------------------------
export type StackGroup = {
    name: string;
    color: string;
    items: string[];
};

export const stack: StackGroup[] = [
    {
        name: 'Frontend',
        color: 'oklch(0.74 0.18 50)',
        items: ['JavaScript', 'TypeScript', 'React', 'Angular', 'Next.js', 'Micro-Frontends (Module Federation)', 'AG Grid', 'Socket.IO', 'Apollo Client', 'Service Workers'],
    },
    {
        name: 'Backend',
        color: 'oklch(0.52 0.20 300)',
        items: ['Node.js (NestJS, Express)', 'REST API', 'GraphQL', 'PostgreSQL', 'MongoDB', 'Redis', 'Elasticsearch'],
    },
    {
        name: 'DevOps & Cloud',
        color: 'oklch(0.91 0.19 112)',
        items: ['AWS', 'Kubernetes', 'Terraform', 'Docker', 'CI/CD (GitLab, TeamCity, CircleCI, ArgoCD)', 'RabbitMQ', 'Kafka', 'Auth0'],
    },
    {
        name: 'AI & Architecture',
        color: 'oklch(0.7 0.14 200)',
        items: ['AI Agents (Mastra)', 'LLMs (Anthropic)', 'Prompt Engineering', 'Evals', 'Microservices'],
    },
];

// ---- Code / profiles ---------------------------------------------------
export const codeProfiles = {
    github: {
        handle: '@rshulgin',
        url: 'https://github.com/rshulgin',
        chart: 'https://ghchart.rshah.org/3fb950/rshulgin',
    },
    gitlab: {
        handle: '@pik94os',
        url: 'https://gitlab.com/pik94os',
    },
};

export type Language = { name: string; pct: number; color: string };

export const languages: Language[] = [
    {name: 'TypeScript', pct: 41, color: 'oklch(0.62 0.16 250)'},
    {name: 'JavaScript', pct: 23, color: 'oklch(0.85 0.16 95)'},
    {name: 'SCSS / CSS', pct: 13, color: 'oklch(0.62 0.18 330)'},
    {name: 'HTML', pct: 9, color: 'oklch(0.68 0.18 40)'},
    {name: 'Shell', pct: 7, color: 'oklch(0.74 0.16 150)'},
    {name: 'Other', pct: 7, color: 'oklch(0.55 0.02 290)'},
];

export const speaking = [
  {language: 'English', level: 'Professional'},
  {language: 'Russian', level: 'Native'},
  {language: 'Hebrew', level: 'Beginner'}
]

export const marqueeItems = [
    'JAVASCRIPT · TYPESCRIPT', 'NODE.JS · NESTJS', 'REACT · NEXT.JS', 'MICRO-FRONTENDS',
    'AWS · KUBERNETES', 'KAFKA · RABBITMQ', 'AI AGENTS · LLMs', 'POSTGRES · ELASTICSEARCH', 'AUTH0 · TERRAFORM',
];
